import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Radio,
    RadioGroup,
    Stack
} from '@chakra-ui/react';

import { AsyncSelector, Selector } from '../shared/asyncSelector';
import { ConfigContext, DataContext } from '../../../core/contexts';

import {
    animalGenders,
    animalTypes,
    animalByTypes
} from '../../../core/animal/animalConstants';
import { LandClass } from '../../../core/land';
import {
    AnimalClass,
    AnimalConstants,
    AnimalInterface
} from '../../../core/animal';
import { AnimalService, LandAnimalService } from '../../../core/services';

export default function AnimalForm(props: {
    data?: AnimalClass;
    onSubmit?: Function;
    setState?: Function;
}): React.ReactElement {
    const { t } = useTranslation();
    const { lands, getLand, updateLand } = useContext(DataContext);

    const animalService = new AnimalService();
    const landAnimalService = new LandAnimalService(lands);

    const [isLoading, setLoading] = useState(true);
    const [isEdit, setEdit] = useState<boolean>(false);
    const [animal, setAnimal] = useState<AnimalInterface>(
        {} as AnimalInterface
    );
    const [animalError, setAnimalError] = useState<Record<string, boolean>>({});

    const isFormValid = (): boolean => {
        const isGenderValid = animalByTypes.single.includes(animal.specie)
            ? animal.gender
            : true;
        const isAmountValid = animalByTypes.group.includes(animal.specie)
            ? animal.type === AnimalConstants.animalTypes.adult
                ? animal.gender
                : animal.malesAmount > 0 || animal.femalesAmount > 0
            : true;

        const isValid =
            animal.externalId?.length &&
            animal.specie &&
            animal.type &&
            animal.land &&
            isGenderValid &&
            isAmountValid;

        if (!isValid) {
            setAnimalError({
                externalId: !animal.externalId,
                gender: !isGenderValid || !isAmountValid,
                specie: !animal.specie,
                type: !animal.type,
                land: !animal.land
            });
        }
        return !!isValid;
    };

    const saveAnimal = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isFormValid()) {
            props.setState && props.setState(true);
            const saved = await animalService.save(new AnimalClass(animal));
            const land = getLand(animal.land) as LandClass;
            await landAnimalService.countAnimalsInLand(land);
            await updateLand(animal.land);

            props.setState && props.setState(false);
            props.onSubmit && props.onSubmit(saved, e);
        }
    };

    useEffect(() => {
        if (props.data?.id) {
            setAnimal(props.data);
            setEdit(props.data !== undefined);
        }
        setLoading(false);
    }, []);

    return !isLoading ? (
        <form id='animalForm' onSubmit={saveAnimal} noValidate>
            {/* EXTERNAL ID */}
            <FormControl
                isRequired
                isInvalid={animalError.externalId}
                isDisabled={isEdit}
            >
                <FormLabel htmlFor='externalId'>
                    {t('animal.externalId')}
                </FormLabel>
                <Input
                    id='externalId'
                    type='text'
                    defaultValue={animal.externalId}
                    onChange={(e) =>
                        setAnimal({ ...animal, externalId: e.target.value })
                    }
                />
                <FormErrorMessage>{t('error.required')}</FormErrorMessage>
            </FormControl>

            {/* INTERNAL ID */}
            <FormControl mt='2'>
                <FormLabel htmlFor='internalId'>
                    {t('animal.internalId')}
                </FormLabel>
                <Input
                    id='internalId'
                    type='text'
                    defaultValue={animal.internalId}
                    onChange={(e) =>
                        setAnimal({ ...animal, internalId: e.target.value })
                    }
                />
            </FormControl>

            {/* BIRTHDAY */}
            <FormControl mt='2'>
                <FormLabel htmlFor='birthDate'>
                    {t('animal.birthDate')}
                </FormLabel>
                <Input
                    id='birthDate'
                    type='date'
                    placeholder={t('animal.birthDate')}
                    defaultValue={animal.birthDate}
                    onChange={(e: any) => {
                        setAnimal({ ...animal, birthDate: e.target.value });
                    }}
                />
            </FormControl>

            {/* SPECIE */}
            <FormControl
                mt='2'
                isRequired
                isInvalid={animalError.specie}
                isDisabled={isEdit}
            >
                <FormLabel mb='0'>
                    {t('animal.specie')} {animal.specie}
                </FormLabel>
                <RadioGroup
                    colorScheme={'green'}
                    // defaultValue={animal.specie}
                    value={animal.specie || ''}
                    onChange={(e: any) => {
                        setAnimal({ ...animal, specie: e });
                    }}
                >
                    <Stack direction='row' justifyContent='space-around'>
                        <ConfigContext.Consumer>
                            {(config) => (
                                <>
                                    {config.animals.species.map(
                                        (specie: any) => (
                                            <Radio value={specie}>
                                                {t(`animal.species.${specie}`)}
                                            </Radio>
                                        )
                                    )}
                                </>
                            )}
                        </ConfigContext.Consumer>
                    </Stack>
                </RadioGroup>
                <FormErrorMessage>{t('error.required')}</FormErrorMessage>
            </FormControl>

            {/* TYPE */}
            <FormControl
                as='fieldset'
                mt='2'
                isRequired
                isInvalid={animalError.type}
                isDisabled={isEdit || !animal.specie}
            >
                <FormLabel as='legend' m='0'>
                    {t('animal.type')}
                </FormLabel>
                <RadioGroup
                    colorScheme={'green'}
                    // defaultValue={animal.type}
                    value={animal.type || ''}
                    onChange={(e: any) => {
                        delete animal?.gender;
                        setAnimal({
                            ...animal,
                            ...{ type: e, gender: undefined }
                        });
                    }}
                >
                    {(!animal.specie ||
                        animalByTypes.single.includes(animal.specie)) && (
                        <Stack direction='row' justifyContent='space-around'>
                            <Radio value={animalTypes.adult}>
                                {t('animal.types.adult')}
                            </Radio>
                            <Radio value={animalTypes.child}>
                                {t('animal.types.child')}
                            </Radio>
                        </Stack>
                    )}
                    {animalByTypes.group.includes(animal.specie) && (
                        <Stack direction='row' justifyContent='space-around'>
                            <Radio value={animalTypes.adult}>
                                {t('animal.types.adult')}
                            </Radio>
                            <Radio value={animalTypes.group}>
                                {t('animal.types.group')}
                            </Radio>
                        </Stack>
                    )}
                </RadioGroup>
                <FormErrorMessage>{t('error.required')}</FormErrorMessage>
            </FormControl>

            {/* GENDER */}
            <FormControl
                mt='2'
                isRequired
                isInvalid={animalError.gender}
                isDisabled={isEdit || !animal.type}
            >
                <FormLabel as='legend' m='0'>
                    {t('animal.gender')}
                </FormLabel>
                {(!animal.type ||
                    [animalTypes.adult, animalTypes.child].includes(
                        animal.type
                    )) && (
                    <RadioGroup
                        colorScheme={'green'}
                        value={animal.gender || ''}
                        defaultValue={animal.gender}
                        onChange={(e: any) => {
                            setAnimal({ ...animal, gender: e });
                        }}
                    >
                        <Stack direction='row' justifyContent='space-around'>
                            <Radio value={animalGenders.male}>
                                {t('animal.genders.male')}
                            </Radio>
                            <Radio value={animalGenders.female}>
                                {t('animal.genders.female')}
                            </Radio>
                        </Stack>
                    </RadioGroup>
                )}

                {[animalTypes.group].includes(animal.type) && (
                    <Stack direction='row' justifyContent='space-around'>
                        <div>
                            <span>{t('animal.genders.males')}</span>
                            <Input
                                id='gendersMale'
                                width='80px'
                                ml='2'
                                type='number'
                                isDisabled={animal.type !== animalTypes.group}
                                defaultValue={animal.malesAmount}
                                onChange={(e) => {
                                    setAnimal({
                                        ...animal,
                                        malesAmount: Number(e.target.value)
                                    });
                                }}
                            />
                        </div>
                        <div>
                            <span>{t('animal.genders.females')}</span>
                            <Input
                                id='gendersFemale'
                                width='80px'
                                ml='2'
                                type='number'
                                isDisabled={animal.type !== animalTypes.group}
                                defaultValue={animal.femalesAmount}
                                onChange={(e) => {
                                    setAnimal({
                                        ...animal,
                                        femalesAmount: Number(e.target.value)
                                    });
                                }}
                            />
                        </div>
                    </Stack>
                )}
                <FormErrorMessage>{t('error.required')}</FormErrorMessage>
            </FormControl>

            {/* LAND */}
            <FormControl mt={2} isRequired isInvalid={animalError.land}>
                <FormLabel mb='0'>{t('animal.land')}</FormLabel>
                <Selector
                    name='land'
                    defaultInputValue={animal?.landData?.name}
                    onChange={(e: any) => {
                        setAnimal({
                            ...animal,
                            land: e.value.id
                            // owner: e.value.ownerUser.id
                        });
                    }}
                    options={lands
                        ?.filter((land) => !land.getLoggedRole().isReader())
                        .map((land: LandClass) => {
                            return {
                                label: land.name,
                                value: land
                            };
                        })}
                ></Selector>
            </FormControl>

            {/* MOTHER */}
            <FormControl mt={2} isDisabled={!animal.specie}>
                <FormLabel mb='0'>{t('animal.mother')}</FormLabel>
                <AsyncSelector
                    name='mother'
                    defaultInputValue={animal.mother?.name}
                    service={landAnimalService}
                    filter={{
                        type: {
                            field: 'gender',
                            comparison: '==',
                            value: animalGenders.female
                        },
                        specie: {
                            field: 'specie',
                            comparison: '==',
                            value: animal.specie
                        }
                    }}
                    onChange={(e: any) => {
                        setAnimal({
                            ...animal,
                            mother: { name: e.label, id: e.value }
                        });
                    }}
                />
            </FormControl>

            {/* FATHER */}
            <FormControl mt={2} isDisabled={!animal.specie}>
                <FormLabel mb='0'>{t('animal.father')}</FormLabel>
                <AsyncSelector
                    name='father'
                    defaultInputValue={animal.father?.name}
                    service={landAnimalService}
                    filter={{
                        type: {
                            field: 'gender',
                            comparison: '==',
                            value: animalGenders.male
                        },
                        specie: {
                            field: 'specie',
                            comparison: '==',
                            value: animal.specie
                        }
                    }}
                    onChange={(e: any) => {
                        setAnimal({
                            ...animal,
                            father: { name: e.label, id: e.value }
                        });
                    }}
                />
            </FormControl>
        </form>
    ) : (
        <></>
    );
}
